import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import style from './ZingNotifications.module.css'
import { isZingNotificationDevice } from '../../../utils/zingNotifications'

const LOW = 20
const CRITICAL = 10
// Re-arm only after a real charge, so readings hovering around 20% don't nag.
const REARM_ABOVE = 25
const SHOW_MS = 6000
const EXIT_MS = 250

const levelFor = (percent) =>
    percent <= CRITICAL ? 'critical' : percent <= LOW ? 'low' : null

// Heads-up when Zing's battery drops to 20% and again at 10%. Shown at most
// once per level per connection; the pill itself can't show a tooltip.
function LowBatteryToast() {
    const percent = useSelector(
        (state) => state.zingNotification.battery?.percent
    )
    const isConnected = useSelector((state) => state.webSerial.isConnected)
    const device = useSelector((state) => state.device)
    const [alert, setAlert] = useState(null) // { level, leaving }
    const warned = useRef(null)

    const eligible =
        isConnected &&
        isZingNotificationDevice(device.connectedDevice, device.deviceVersion)

    useEffect(() => {
        if (!eligible || percent == null) {
            warned.current = null
            setAlert(null)
            return
        }
        if (percent > REARM_ABOVE) {
            warned.current = null
            return
        }
        const level = levelFor(percent)
        if (!level || warned.current === level || warned.current === 'critical')
            return
        warned.current = level
        setAlert({ level, leaving: false })
    }, [eligible, percent])

    useEffect(() => {
        if (!alert) return undefined
        const timer = setTimeout(
            () => setAlert(alert.leaving ? null : { ...alert, leaving: true }),
            alert.leaving ? EXIT_MS : SHOW_MS
        )
        return () => clearTimeout(timer)
    }, [alert])

    if (!alert || percent == null) return null

    const critical = alert.level === 'critical'
    return (
        <div
            className={`${style.batteryToast} ${
                critical ? style.batteryToastCritical : ''
            } ${alert.leaving ? style.batteryToastLeaving : ''}`}
            role={critical ? 'alert' : 'status'}
            aria-live={critical ? 'assertive' : 'polite'}
            data-testid="zing-low-battery"
        >
            <span className={style.toastBattery} aria-hidden="true">
                <span className={style.batteryRow}>
                    <span className={style.batteryBody}>
                        <span
                            className={style.batteryFill}
                            style={{ width: `${percent}%` }}
                        />
                    </span>
                    <span className={style.batteryCap} />
                </span>
            </span>
            <span className={style.toastBody}>
                <strong className={style.toastTitle}>
                    {critical
                        ? 'Battery almost empty'
                        : "Zing's battery is low"}
                </strong>
                <span className={style.toastText}>
                    {critical
                        ? `Only ${percent}% left. Please charge Zing now.`
                        : `${percent}% left. Charge Zing soon so it doesn't stop mid-action.`}
                </span>
            </span>
            <button
                type="button"
                className={style.toastClose}
                aria-label="Dismiss battery warning"
                onClick={() => setAlert({ ...alert, leaving: true })}
            >
                ×
            </button>
            <span className={style.toastTimer} aria-hidden="true" />
        </div>
    )
}

export default LowBatteryToast
